import React from "react";
import { connect } from "react-redux";
import DOMPurify from "isomorphic-dompurify";
import Lottie from "lottie-react";

import freeUpgradeModalShield from "@components/common/Lottie/animations/free-upgrade-modal-shield.json";
import { getWidgetSpecificProps } from "@utils/index";
import { resolvePath } from "@utils/resolvePath";
import { CommonProps } from "@utils/interface";
import { getInrFormat } from "@utils/inrFormatter";
import {
  Heading,
  IllustrationWrap,
  Item,
  ItemList,
  ModalContent,
  OfferPanel,
  PriceLabel,
  PriceSummary,
  PriceValues,
  Section,
  SectionLabel,
  Sparkle,
  Subtext
} from "./styles";

interface ModalSection {
  id: string;
  title: string;
  items: Array<{ text: string }>;
}

interface WidgetState {
  configuration?: {
    normalisedConfig?: Record<string, unknown>;
  };
}

interface ModalContentConfig {
  heading?: string;
  subtext?: string;
  icon_url?: string;
  price_summary?: {
    paid_label?: string;
    free_label?: string;
  };
  sections?: {
    free_addons?: {
      title?: string;
      item_template?: string;
      sum_insured_template?: string;
    };
    addon_upgrades?: {
      title?: string;
      item_template?: string;
      item_template_overrides?: Record<string, string>;
      addon_name_overrides?: Record<string, string>;
    };
  };
}

interface RenewalFreeUpgradeModalProps extends CommonProps {
  modalData?: {
    show?: boolean;
    heading?: string;
    subtext?: string;
    cta_type?: "continue" | null;
    icon_url?: string;
    price_details?: {
      payable_amount?: number | string | null;
      strike_through_amount?: number | string | null;
    };
    free_addons?: Array<{
      id?: string;
      is_selected?: boolean;
      display_name?: string;
      sum_insured?: number | string | null;
      savings_amount?: number | string | null;
    }>;
    addon_upgrades?: Array<{
      id?: string;
      is_selected?: boolean;
      addon_selected?: boolean;
      display_name?: string;
      current_sum_insured?: number | string | null;
      upgraded_sum_insured?: number | string | null;
      current_variant_label?: string | null;
      upgraded_variant_label?: string | null;
      savings_amount?: number | string | null;
    }>;
    sections?: ModalSection[];
  };
  modalContentConfig?: ModalContentConfig;
}

const ILLUSTRATION_SIZE = 140;

const formatCurrency = (amount?: number | string | null) => {
  if (amount === undefined || amount === null || amount === "") {
    return "";
  }
  if (Number(amount) === 0) {
    return "₹0";
  }
  const formattedAmount = getInrFormat(amount);
  return formattedAmount ? `₹${formattedAmount}` : "";
};

const formatCoverAmount = (amount?: number | string | null) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return "";
  }
  if (numericAmount % 100000 === 0) {
    return `${numericAmount / 100000} lakh`;
  }
  return formatCurrency(amount);
};

const applyTemplate = (
  template: string = "",
  values: Record<string, string>
) =>
  Object.keys(values).reduce(
    (text, key) => text.replace(new RegExp(`{${key}}`, "g"), values[key]),
    template
  );

const PA_OWNER_CAR = "pa_owner_car";
const RSA_COMBO_CAR = "rsa_outstation_keyprotect_combo_car";
const PA_FREE_SUM_FALLBACK = "15 lakh";

const coverLabel = (
  label?: string | null,
  amount?: number | string | null
) => {
  if (label && !/^\d+(\.\d+)?$/.test(String(label).trim())) {
    return String(label).replace(/^₹\s*/, "");
  }
  return (formatCoverAmount(amount) || String(label || "")).replace(/^₹\s*/, "");
};

export const getRenewalFreeUpgradeModalSubtext = (
  modalData?: RenewalFreeUpgradeModalProps["modalData"]
) => {
  const addonUpgrades = modalData?.addon_upgrades || [];
  const rsaUpgrade = addonUpgrades.find((addon) => addon.id === RSA_COMBO_CAR);
  if (rsaUpgrade) {
    return "Previously your towing was capped at 40 kms. Now it’s unlimited";
  }

  const paUpgrade = addonUpgrades.find((addon) => addon.id === PA_OWNER_CAR);
  if (paUpgrade) {
    const current = coverLabel(
      paUpgrade.current_variant_label,
      paUpgrade.current_sum_insured
    );
    const upgraded = coverLabel(
      paUpgrade.upgraded_variant_label,
      paUpgrade.upgraded_sum_insured
    );
    if (current && upgraded) {
      return `Personal Accident Coverage has been upgraded from ₹${current} to ₹${upgraded}.`;
    }
  }

  const paFreeAddon = (modalData?.free_addons || []).find(
    (addon) => addon.id === PA_OWNER_CAR
  );
  if (paFreeAddon) {
    const sumInsured =
      coverLabel(null, paFreeAddon.sum_insured) || PA_FREE_SUM_FALLBACK;
    return `Personal Accident Coverage of ₹${sumInsured} has been added.`;
  }

  return "";
};

const getPriceSummary = (
  priceDetails?: NonNullable<
    RenewalFreeUpgradeModalProps["modalData"]
  >["price_details"],
  priceSummaryConfig?: ModalContentConfig["price_summary"]
) => {
  const payableAmount = Number(priceDetails?.payable_amount);
  const payable = formatCurrency(priceDetails?.payable_amount);
  const strikeThrough = formatCurrency(priceDetails?.strike_through_amount);

  if (!Number.isFinite(payableAmount) || !payable || !strikeThrough) {
    return null;
  }

  return {
    label:
      payableAmount === 0
        ? priceSummaryConfig?.free_label || "What you pay"
        : priceSummaryConfig?.paid_label || "You pay same as last year",
    payable,
    strikeThrough
  };
};

const getForceQaModalData = ():
  | RenewalFreeUpgradeModalProps["modalData"]
  | null => {
  if (typeof window === "undefined" || process.env.NODE_ENV === "production") {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  if (params.get("force_free_upgrade_modal") !== "1") {
    return null;
  }
  const variant = params.get("force_free_upgrade_variant") || "continue";
  if (variant === "free_covers") {
    return {
      show: true,
      cta_type: "continue",
      subtext: "Personal Accident Coverage of ₹15 lakh has been added.",
      price_details: {
        payable_amount: 0,
        strike_through_amount: 399
      },
      free_addons: [
        {
          id: "pa_owner_car",
          is_selected: true,
          display_name: "Personal Accident Cover",
          sum_insured: 1500000,
          savings_amount: 750
        },
        {
          id: "road_side_assistance_car",
          is_selected: true,
          display_name: "Roadside Assistance",
          sum_insured: null,
          savings_amount: 199
        }
      ],
      sections: [
        {
          id: "free_addons",
          title: "Free covers",
          items: [
            { text: "Personal Accident Cover" },
            { text: "Roadside Assistance" }
          ]
        }
      ]
    };
  }
  return {
    show: true,
    cta_type: "continue",
    subtext: "Previously your towing was capped at 40 kms. Now it’s unlimited",
    price_details:
      variant === "same_price_399"
        ? { payable_amount: 399, strike_through_amount: 799 }
        : { payable_amount: 299, strike_through_amount: 999 },
    sections: [
      {
        id: "addon_upgrades",
        title: "Upgraded cover",
        items: [
          {
            text:
              "Extra Car Protect: Unlimited distance towing <s>40 km towing</s>"
          }
        ]
      }
    ]
  };
};

export const buildRenewalFreeUpgradeSections = (
  modalData?: RenewalFreeUpgradeModalProps["modalData"],
  modalContentConfig?: ModalContentConfig
): ModalSection[] => {
  const sectionConfig = modalContentConfig?.sections;
  if (!sectionConfig && Array.isArray(modalData?.sections)) {
    return modalData.sections;
  }

  const sections: ModalSection[] = [];
  const freeAddonConfig = sectionConfig?.free_addons;
  const freeAddons = modalData?.free_addons || [];
  const freeAddonItems = freeAddons
    .map((addon) => {
      const addonName = addon?.display_name || "";
      const savingsAmount = formatCurrency(addon?.savings_amount);
      const freeAddonItemTemplate = freeAddonConfig?.item_template;
      if (!addonName || !freeAddonItemTemplate) {
        return null;
      }
      if (!savingsAmount) {
        return null;
      }

      const sumInsured = formatCurrency(addon?.sum_insured);
      const sumInsuredText = sumInsured
        ? applyTemplate(freeAddonConfig?.sum_insured_template, {
            sum_insured: sumInsured
          })
        : "";

      return {
        text: applyTemplate(freeAddonItemTemplate, {
          addon_name: addonName,
          sum_insured_text: sumInsuredText,
          savings_amount: savingsAmount
        })
      };
    })
    .filter(Boolean) as Array<{ text: string }>;

  const freeAddonTitle = freeAddonConfig?.title;

  if (freeAddonItems.length > 0 && freeAddonTitle) {
    sections.push({
      id: "free_addons",
      title: freeAddonTitle,
      items: freeAddonItems
    });
  }

  const addonUpgradeConfig = sectionConfig?.addon_upgrades;
  const addonUpgrades = modalData?.addon_upgrades || [];
  const addonUpgradeItems = addonUpgrades
    .map((addon) => {
      const addonId = addon?.id || "";
      const addonName =
        addonUpgradeConfig?.addon_name_overrides?.[addonId] ||
        addon?.display_name ||
        "";
      const currentSumInsured = formatCoverAmount(addon?.current_sum_insured);
      const upgradedSumInsured = formatCoverAmount(addon?.upgraded_sum_insured);
      const savingsAmount = formatCurrency(addon?.savings_amount);
      const itemTemplate = addonUpgradeConfig?.item_template_overrides?.[addonId] ||
        addonUpgradeConfig?.item_template;

      if (!addonName || !itemTemplate) {
        return null;
      }

      const currentCover =
        addon?.current_variant_label || currentSumInsured;
      const upgradedCover =
        addon?.upgraded_variant_label || upgradedSumInsured;
      const text = applyTemplate(itemTemplate, {
        addon_name: addonName,
        current_sum_insured: currentCover,
        upgraded_sum_insured: upgradedCover,
        current_variant_label: currentCover,
        upgraded_variant_label: upgradedCover,
        current: currentCover,
        upgraded: upgradedCover,
        old_cover: currentCover,
        upgraded_cover: upgradedCover,
        savings_amount: savingsAmount
      });

      if (/{[^}]+}/.test(text)) {
        return null;
      }

      return {
        text
      };
    })
    .filter(Boolean) as Array<{ text: string }>;

  const addonUpgradeTitle = addonUpgradeConfig?.title;

  if (addonUpgradeItems.length > 0 && addonUpgradeTitle) {
    sections.push({
      id: "addon_upgrades",
      title: addonUpgradeTitle,
      items: addonUpgradeItems
    });
  }

  return sections;
};

const renderSection = (section: ModalSection) => (
  <Section key={section.id}>
    <SectionLabel>
      <span>{section.title}</span>
    </SectionLabel>
    <ItemList>
      {(section.items || []).map((item, index) => (
        <Item
          key={`${section.id}-${index}`}
          data-section-id={section.id}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(item.text)
          }}
        />
      ))}
    </ItemList>
  </Section>
);

const mapStateToProps = function (state: WidgetState, ownProps: CommonProps) {
  return getWidgetSpecificProps(
    state.configuration?.normalisedConfig,
    ownProps.id || ""
  );
};

const RenewalFreeUpgradeModal = (
  props: RenewalFreeUpgradeModalProps
) => {
  const { id, is_visible, modalData, modalContentConfig } = props || {};
  const qaModalData = getForceQaModalData();
  const effectiveModalData = qaModalData || (modalData?.show ? modalData : null);
  const sections = buildRenewalFreeUpgradeSections(
    effectiveModalData || undefined,
    qaModalData ? undefined : modalContentConfig
  );
  const heading = modalContentConfig?.heading || effectiveModalData?.heading;
  const subtext =
    qaModalData?.subtext ||
    getRenewalFreeUpgradeModalSubtext(effectiveModalData || undefined);
  const priceSummary = getPriceSummary(
    effectiveModalData?.price_details,
    modalContentConfig?.price_summary
  );

  if ((id && !is_visible && !qaModalData) || !effectiveModalData?.show || sections.length === 0) {
    return null;
  }

  return (
    <ModalContent>
      <IllustrationWrap>
        <Lottie
          animationData={freeUpgradeModalShield}
          loop
          autoplay
          style={{ width: ILLUSTRATION_SIZE, height: ILLUSTRATION_SIZE }}
        />
      </IllustrationWrap>
      <Heading>
        <span>{heading}</span>
      </Heading>
      {subtext && <Subtext>{subtext}</Subtext>}
      <OfferPanel>
        <Sparkle
          data-position="top-left"
          src={resolvePath("/images/renewal/sparkle-tl.svg")}
          alt=""
          aria-hidden="true"
        />
        <Sparkle
          data-position="bottom-right"
          src={resolvePath("/images/renewal/sparkle-br.svg")}
          alt=""
          aria-hidden="true"
        />
        {priceSummary && (
          <PriceSummary>
            <PriceLabel>{priceSummary.label}</PriceLabel>
            <PriceValues>
              <strong>{priceSummary.payable}</strong>
              <s>{priceSummary.strikeThrough}</s>
            </PriceValues>
          </PriceSummary>
        )}
        {!priceSummary && sections.map(renderSection)}
      </OfferPanel>
    </ModalContent>
  );
};

export default connect(mapStateToProps)(RenewalFreeUpgradeModal);
